import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrackRecordStrip() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const retriesRef = useRef(0);

  useEffect(() => {
    const load = () => axios.get(`${BACKEND_URL}/api/track-record?days=90&limit=8`)
      .then((r) => {
        setData(r.data);
        // If backend served the "warming" placeholder (cold snapshot),
        // retry in 5s to pick up the real numbers once the background
        // rebuild finishes. Bounded to 3 retries.
        if (r.data?.warming && retriesRef.current < 3) {
          retriesRef.current += 1;
          setTimeout(load, 5000);
        }
      })
      .catch(() => setErr(true));
    load();
  }, []);

  // Treat warming placeholder as "still loading" so we keep the skeleton up.
  const isReady = data && !data.warming && (data.total_alerts ?? 0) > 0;

  if (!isReady) {
    // Loading / errored — render the shell so the section stays anchored.
    return (
      <section data-testid="landing-track-record-strip-skeleton">
        <div className="max-w-6xl mx-auto px-6 lg:px-12 py-10">
          <div className="flex flex-col items-center text-center gap-4 mb-6">
            <div>
              <h2 className="font-serif-hero text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-white">The record is public.</h2>
              <div className="mt-5 font-serif-hero text-7xl sm:text-8xl text-neutral-600 animate-pulse">—%</div>
              <p className="mt-2 text-sm text-neutral-400">
                {err
                  ? "Couldn't reach the desk right now. Refresh in a moment."
                  : "Replaying every approved alert against real market data…"}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[..."abcde"].map((k) => (
              <div key={k}
                   className="rounded-xl p-4 animate-pulse border border-white/10 bg-white/[0.03] text-center">
                <div className="text-sm text-neutral-500">—</div>
                <div className="font-serif-hero text-4xl mt-1 text-neutral-600">—</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section data-testid="landing-track-record-strip">
      <div className="max-w-6xl mx-auto px-6 lg:px-12 py-10">
        <div className="flex flex-col items-center text-center gap-4 mb-6">
          <div>
            <h2 className="font-serif-hero text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-white">The record is public.</h2>
            <div className="mt-5 flex items-baseline justify-center gap-3 flex-wrap" data-testid="tr-strip-winrate">
              <span className="font-serif-hero text-7xl sm:text-8xl text-white tabular-nums">{data.win_rate}%</span>
              <span className="text-lg text-neutral-400">win rate · last 90 days</span>
            </div>
            <p className="mt-2 text-sm" style={{ color: "var(--mb-ink-muted)" }}>
              {data.wins} wins · {data.losses} losses · {data.moon_hits} moons hit · all replayed against real market data.
            </p>
          </div>
          <Link
            to="/track-record"
            className="text-sm font-semibold text-violet-300 hover:underline underline-offset-4 inline-flex items-center gap-1"
            data-testid="view-full-track-record"
          >
            View every trade <ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Mini label="Total Alerts" val={data.total_alerts} />
          <Mini label="Wins" val={data.wins} pos />
          <Mini label="Losses" val={data.losses} neg />
          <Mini label="Moons hit" val={data.moon_hits} />
          <Mini label="Avg Return" val={`${data.avg_return_pct >= 0 ? "+" : ""}${data.avg_return_pct}%`} pos={data.avg_return_pct >= 0} />
        </div>

        {data.alerts && data.alerts.length > 0 && (
          <div className="mt-6 mb-scroll-fade">
            <div className="overflow-x-auto mb-scroll-hud pb-3" data-testid="tr-strip-scroller">
              <div className="flex gap-2 w-max mx-auto px-1">
                {data.alerts.slice(0, 8).map((a) => {
                  const o = a.outcome || {};
                  const isWin = (o.status || "").startsWith("WIN");
                  const isLoss = (o.status || "").startsWith("LOSS");
                  const ret = Number(o.return_pct) || 0;
                  const accent = (isWin || isLoss) ? (ret >= 0 ? "#34D399" : "#F87171") : "#C4B5FD";
                  const exitLabel = { TP: "target", MOON: "moon", TIME: "time", SL: "stop" }[(o.status || "").replace("WIN_", "").replace("LOSS_", "")] || "open";
                  return (
                    <div
                      key={a.id}
                      className="flex items-center gap-3 px-4 py-2 rounded-full border bg-black/40 backdrop-blur-md"
                      style={{ borderColor: `${accent}55` }}
                      data-testid={`tr-strip-${a.symbol}`}
                    >
                      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: accent }} />
                      <span className="font-semibold text-sm text-white">{a.symbol}</span>
                      <span className="text-sm font-semibold tabular-nums" style={{ color: accent }}>
                        {o.return_pct >= 0 ? "+" : ""}{o.return_pct}%
                      </span>
                      <span className="text-xs text-neutral-400">{exitLabel}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

const Mini = ({ label, val, pos, neg }) => (
  <div className="rounded-xl p-4 border border-white/10 bg-black/40 backdrop-blur-md text-center">
    <div className="text-sm text-neutral-400">{label}</div>
    <div className="font-serif-hero text-4xl mt-1 tabular-nums" style={{ color: pos ? "#34D399" : neg ? "#F87171" : "#FFFFFF" }}>{val}</div>
  </div>
);
